import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  Lightbulb,
  ArrowRight,
  FileText,
  Target,
  Layers,
  Construction,
  Wrench,
  Settings,
  Gauge,
  AlertTriangle,
  Cog,
  HardDrive,
  Activity,
  Ruler,
  ExternalLink
} from 'lucide-react';

const DrillingEquipmentGuide = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  // Table of contents
  const sections = [
    { id: 'section-1', title: "Comprendre la chaîne du matériel" },
    { id: 'section-2', title: "Les machines de forage" },
    { id: 'section-3', title: "Les outils de forage" },
    { id: 'section-4', title: "Tiges, tubages et accessoires" },
    { id: 'section-5', title: "L'instrumentation" },
    { id: 'section-6', title: "Louer ou acheter ?" },
    { id: 'section-7', title: "Questions fréquentes" },
  ];

  const faqs = [
    {
      question: "Comment choisir son outil de forage ?",
      answer: "La nature du terrain est le critère n°1. Il est recommandé de réaliser une étude de sol préalable pour caractériser la géologie avant de sélectionner le matériel : tarière en sols meubles, marteau fond de trou ou PDC en roche dure, carottier pour prélever."
    },
    {
      question: "Quelle différence entre une foreuse et une sondeuse ?",
      answer: "La foreuse de fondations est conçue pour réaliser un ouvrage (pieu, micropieu), avec puissance et couple élevés. La sondeuse géotechnique sert à reconnaître le terrain (sondages, essais, prélèvements), avec une priorité donnée à la mobilité."
    },
    {
      question: "Faut-il faire étalonner son matériel de mesure ?",
      answer: "Oui, impérativement et régulièrement. Un capteur mal étalonné produit des enregistrements faux, ce qui invalide le contrôle qualité et peut poser problème en cas de litige."
    },
    {
      question: "Peut-on louer du matériel d'instrumentation ?",
      answer: "Oui. C'est même souvent la solution la plus sûre : le loueur spécialisé fournit un matériel étalonné, maintenu et à jour, sans que vous ayez à gérer le suivi métrologique."
    }
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Schema.org JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Matériel de forage : machines, outils et instrumentation | Guide complet",
    "description": "Foreuse, tarière, carottier, trépan, tubage, enregistreur de paramètres : le guide complet du matériel de forage géotechnique. Quel outil pour quel terrain ?",
    "image": "https://geots.consulting/og-images/guide-materiel-forage.jpg",
    "author": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://geots.consulting/logo.png"
      }
    },
    "datePublished": "2026-07-06",
    "dateModified": "2026-07-06"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Chain elements
  const chainElements = [
    { number: 1, title: "La machine", desc: "Foreuse, sondeuse, carotteuse : elle fournit la puissance, le couple et la poussée.", icon: Construction },
    { number: 2, title: "L'outil de forage", desc: "Tarière, trépan, carottier, marteau fond de trou : c'est lui qui attaque le terrain.", icon: Cog },
    { number: 3, title: "Le train de tiges", desc: "Tiges, raccords, tubages : ils transmettent l'effort et maintiennent le trou.", icon: Ruler },
    { number: 4, title: "Les fluides", desc: "Boues, pompes, malaxeurs, dessableurs : ils évacuent les déblais et stabilisent.", icon: Settings },
    { number: 5, title: "L'instrumentation", desc: "Enregistreur de paramètres, capteurs : elle mesure, contrôle et trace l'exécution.", icon: Gauge },
  ];

  // Machines data
  const machines = [
    {
      name: "Foreuses de fondations",
      description: "Machines lourdes sur chenilles, destinées aux pieux, micropieux, parois. Elles délivrent un couple élevé et des profondeurs importantes.",
      hasGuide: true,
      guideLink: "/guides/comment-choisir-foreuse-geotechnique",
      color: "blue"
    },
    {
      name: "Sondeuses géotechniques",
      description: "Machines de reconnaissance, plus légères, conçues pour les sondages, essais et prélèvements. Elles privilégient la mobilité et l'accès aux sites contraints.",
      hasGuide: false,
      color: "green"
    },
    {
      name: "Carotteuses",
      description: "Spécialisées dans la récupération d'échantillons intacts (carottes) pour analyse en laboratoire, notamment pour confirmer la présence et la nature du substratum rocheux.",
      hasGuide: false,
      color: "purple"
    },
    {
      name: "Tarières sur porteur",
      description: "Montées sur pelle ou mini-pelle, elles conviennent aux forages courts en sols meubles.",
      hasGuide: false,
      color: "orange"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Matériel de forage : machines, outils et instrumentation | GEOTS</title>
        <meta name="description" content="Foreuse, tarière, carottier, trépan, tubage, enregistreur de paramètres : le guide complet du matériel de forage géotechnique. Quel outil pour quel terrain ?" />
        <link rel="canonical" href="https://geots.consulting/guides/materiel-de-forage" />
        <meta property="og:title" content="Matériel de forage : machines, outils et instrumentation | Guide complet" />
        <meta property="og:description" content="Foreuse, tarière, carottier, trépan, tubage, enregistreur de paramètres : le guide complet du matériel de forage géotechnique." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://geots.consulting/guides/materiel-de-forage" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/guides" className="hover:text-[#635BFF]">Guides</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">Matériel de forage</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  GUIDE COMPLET
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Mis à jour le 6 juillet 2026
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  15 min de lecture
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Matériel de forage :
                <span className="text-[#00D4FF]"> le guide complet (machines, outils et instrumentation)</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Foreuse, tarière, carottier, trépan, tubage, enregistreur de paramètres… Le matériel de forage 
                recouvre une chaîne d'équipements où chaque maillon compte. Comment s'y retrouver, 
                et surtout comment choisir selon son chantier et son terrain ? Panorama complet.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/shop?listing_type=location" 
                  className="bg-[#635BFF] hover:bg-[#5248e5] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Louer du matériel
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/shop?listing_type=neuf,occasion" 
                  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Acheter du matériel
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-xl shadow-sm border p-6">
                <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#635BFF]" />
                  Sommaire
                </h3>
                <nav className="space-y-2">
                  {sections.map((section, index) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-start gap-2 ${
                        activeSection === section.id
                          ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-xs font-bold text-gray-400 mt-0.5">{index + 1}.</span>
                      {section.title}
                    </button>
                  ))}
                </nav>

                {/* Links to related guides */}
                <div className="mt-6 pt-6 border-t">
                  <p className="text-xs text-gray-500 mb-2">Guides connexes</p>
                  <Link 
                    to="/guides/comment-choisir-foreuse-geotechnique"
                    className="text-sm text-[#635BFF] hover:underline flex items-center gap-1 mb-2"
                  >
                    <Wrench className="w-4 h-4" />
                    Choisir une foreuse
                  </Link>
                  <Link 
                    to="/guides/comment-choisir-etude-geotechnique"
                    className="text-sm text-[#635BFF] hover:underline flex items-center gap-1 mb-2"
                  >
                    <FileText className="w-4 h-4" />
                    Choisir son étude de sol
                  </Link>
                  <Link 
                    to="/guides/comment-choisir-fondations-speciales"
                    className="text-sm text-[#635BFF] hover:underline flex items-center gap-1"
                  >
                    <Layers className="w-4 h-4" />
                    Fondations spéciales
                  </Link>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 max-w-3xl">
              <article className="prose prose-lg max-w-none">
                
                {/* Section 1 - Chain */}
                <section id="section-1" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">1</span>
                    Comprendre la chaîne du matériel de forage
                  </h2>
                  
                  <p className="text-gray-700 mb-6">
                    Parler de « matériel de forage » au singulier est trompeur. Sur un chantier, il s'agit en réalité 
                    d'une <strong>chaîne d'équipements complémentaires</strong> :
                  </p>

                  <div className="space-y-4 mb-8">
                    {chainElements.map((elem) => (
                      <div key={elem.number} className="bg-white rounded-xl border p-4 flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#635BFF] to-[#00D4FF] flex items-center justify-center text-white font-bold flex-shrink-0">
                          {elem.number}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-[#0A2540] flex items-center gap-2">
                            <elem.icon className="w-4 h-4 text-[#635BFF]" />
                            {elem.title}
                          </h4>
                          <p className="text-sm text-gray-600">{elem.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-xl p-6">
                    <Lightbulb className="w-8 h-8 text-[#00D4FF] mb-3" />
                    <p className="text-[#0A2540]">
                      <strong>Point clé :</strong> Un maillon mal choisi, et c'est toute la performance du chantier 
                      qui en pâtit. C'est pourquoi le matériel se raisonne <strong>en système</strong>, jamais isolément.
                    </p>
                  </div>
                </section>

                {/* Section 2 - Machines */}
                <section id="section-2" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">2</span>
                    Les machines de forage
                  </h2>

                  <div className="space-y-4 mb-8">
                    {machines.map((machine, i) => (
                      <div key={i} className={`bg-white rounded-xl border-l-4 ${
                        machine.color === 'blue' ? 'border-l-blue-500' :
                        machine.color === 'green' ? 'border-l-green-500' :
                        machine.color === 'purple' ? 'border-l-purple-500' :
                        'border-l-orange-500'
                      } shadow-sm p-6`}>
                        <h3 className="text-xl font-bold text-[#0A2540] mb-2">{machine.name}</h3>
                        <p className="text-gray-700 mb-3">{machine.description}</p>
                        {machine.hasGuide && (
                          <Link 
                            to={machine.guideLink}
                            className="inline-flex items-center gap-2 text-[#635BFF] font-semibold hover:underline"
                          >
                            <ArrowRight className="w-4 h-4" />
                            Voir notre guide dédié pour bien choisir sa foreuse
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                    <AlertTriangle className="w-6 h-6 text-yellow-600 mb-2" />
                    <p className="text-gray-700">
                      <strong>Norme à connaître :</strong> La sécurité des machines de forage et de fondation 
                      est encadrée par la norme <strong>NF EN 16228</strong>.
                    </p>
                  </div>
                </section>

                {/* Section 3 - Tools */}
                <section id="section-3" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">3</span>
                    Les outils de forage : le choix dicté par le terrain
                  </h2>

                  <p className="text-gray-700 mb-6">
                    C'est ici que se joue l'essentiel. <strong>La nature géologique du terrain oriente le choix 
                    de l'outil</strong>, pas l'inverse.
                  </p>

                  <div className="bg-white rounded-xl border overflow-hidden mb-8">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]">Terrain</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]">Outil adapté</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {[
                          ["Sols meubles (sables, argiles, limons)", "Tarière (hélicoïdale, continue, creuse), drag bit"],
                          ["Sols mous à non consolidés", "Drag bits, tarières"],
                          ["Formations tendres à moyennement dures (calcaire, craie)", "Tricône, trilame"],
                          ["Roches dures", "Marteau fond de trou (MFT), outils PDC"],
                          ["Prélèvement d'échantillons", "Carottiers (simple, double, triple tube, SPT)"],
                        ].map(([terrain, outil], i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-700">{terrain}</td>
                            <td className="px-4 py-3">
                              <span className="text-[#635BFF] font-medium">{outil}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="space-y-4">
                    {[
                      { name: "La tarière", desc: "L'outil de base des sols meubles. On distingue la tarière hélicoïdale continue, la tarière creuse (hollow stem auger) qui sert aussi de tubage, et la tarière à injection." },
                      { name: "Le marteau fond de trou (MFT)", desc: "Fragmente la roche par percussion rotative. Robuste, longue durée de vie, il s'impose dans les formations dures." },
                      { name: "Les outils PDC", desc: "Diamant polycristallin : durée de vie supérieure et bons rendements dans les formations très dures." },
                      { name: "Les carottiers", desc: "Permettent la récupération d'échantillons intacts, indispensables aux analyses géotechniques. Couronnes au carbure de tungstène ou diamantées selon la dureté." },
                    ].map((tool, i) => (
                      <div key={i} className="bg-white rounded-lg border p-4">
                        <h4 className="font-semibold text-[#0A2540] mb-1">{tool.name}</h4>
                        <p className="text-sm text-gray-600">{tool.desc}</p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 4 - Accessories */}
                <section id="section-4" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">4</span>
                    Tiges, tubages et accessoires
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Souvent négligés, ce sont pourtant des <strong>maillons critiques</strong> :
                  </p>

                  <div className="space-y-3 mb-8">
                    {[
                      { title: "Tiges de forage", desc: "Transmettent couple et poussée à l'outil." },
                      { title: "Raccords", desc: "Vérifier impérativement la compatibilité des filetages et dimensions d'emmanchement avant assemblage." },
                      { title: "Tubages", desc: "Maintiennent les parois du forage dans les terrains instables." },
                      { title: "Périphériques", desc: "Pompes d'injection, malaxeurs, boues de forage, sondes de niveau d'eau, obturateurs, clés à chaîne et à griffe." },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3 bg-white rounded-lg border p-4">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-semibold text-[#0A2540]">{item.title} : </span>
                          <span className="text-gray-600">{item.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                    <AlertTriangle className="w-6 h-6 text-red-500 mb-2" />
                    <p className="text-gray-700">
                      <strong>⚠️ Point de vigilance raccords :</strong> Une incompatibilité de filetages ou de 
                      dimensions d'emmanchement, et c'est le blocage sur chantier. Vérifiez toujours avant assemblage.
                    </p>
                  </div>
                </section>

                {/* Section 5 - Instrumentation */}
                <section id="section-5" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">5</span>
                    L'instrumentation : le maillon souvent oublié
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Un chantier de forage moderne ne se contente plus de creuser : il doit <strong>prouver</strong> ce 
                    qu'il a fait. C'est le rôle de l'instrumentation.
                  </p>

                  <div className="bg-white rounded-xl border-l-4 border-l-[#635BFF] shadow-sm p-6 mb-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
                        <Gauge className="w-6 h-6 text-[#635BFF]" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#0A2540] mb-2">L'enregistreur de paramètres (DAC)</h3>
                        <p className="text-gray-700 mb-3">
                          Mesure et enregistre en temps réel : profondeur, vitesse, couple, pression, volumes injectés. 
                          Il constitue la <strong>trace objective de l'exécution</strong> — souvent le seul témoin 
                          fiable d'un ouvrage enterré.
                        </p>
                        <p className="text-sm text-gray-500 mb-3">
                          <strong>Norme :</strong> L'enregistrement des paramètres de forage fait l'objet de la norme 
                          <strong> NF EN ISO 22476-15</strong>.
                        </p>
                        <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                          <Activity className="w-4 h-4 inline mr-2 text-[#635BFF]" />
                          Guide dédié à l'enregistreur de paramètres — <span className="italic">Bientôt disponible</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-xl p-6">
                    <Lightbulb className="w-8 h-8 text-[#00D4FF] mb-3" />
                    <p className="text-[#0A2540]">
                      <strong>Étalonnage obligatoire :</strong> Les capteurs et instruments de mesure (pression, couple, 
                      inclinaison, niveau d'eau) doivent être <strong>étalonnés régulièrement</strong> : un capteur 
                      dérivé fausse tout le contrôle qualité. Un matériel non calibré ne vaut pas mieux qu'une absence de mesure.
                    </p>
                  </div>
                </section>

                {/* Section 6 - Buy vs Rent */}
                <section id="section-6" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">6</span>
                    Louer ou acheter son matériel de forage ?
                  </h2>

                  <div className="bg-white rounded-xl border overflow-hidden mb-8">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]"></th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-blue-600">Location</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-green-600">Achat</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-700">Pertinent si</td>
                          <td className="px-4 py-3 text-gray-600">Chantier ponctuel, besoin spécifique, pic d'activité</td>
                          <td className="px-4 py-3 text-gray-600">Usage régulier, activité récurrente</td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-700">Avantages</td>
                          <td className="px-4 py-3 text-gray-600">Pas d'immobilisation de capital, matériel révisé, pas de stockage</td>
                          <td className="px-4 py-3 text-gray-600">Disponibilité immédiate, amortissement long terme</td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-700">Vigilance</td>
                          <td className="px-4 py-3 text-gray-600">Disponibilité, délais, coût cumulé si longue durée</td>
                          <td className="px-4 py-3 text-gray-600">Maintenance, étalonnage, stockage, obsolescence</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                    <CheckCircle2 className="w-6 h-6 text-green-500 mb-2" />
                    <p className="text-gray-700">
                      <strong>Atout location pour l'instrumentation :</strong> Le matériel loué chez un spécialiste est 
                      <strong> étalonné et vérifié</strong>, ce qui garantit la validité des mesures sans que vous ayez 
                      à gérer le suivi métrologique.
                    </p>
                  </div>
                </section>

                {/* Section 7 - FAQ */}
                <section id="section-7" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">7</span>
                    Questions fréquentes
                  </h2>

                  <div className="space-y-3">
                    {faqs.map((faq, i) => (
                      <div key={i} className="bg-white rounded-xl border overflow-hidden">
                        <button
                          onClick={() => setOpenFaq(openFaq === i ? null : i)}
                          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-semibold text-[#0A2540]">{faq.question}</span>
                          {openFaq === i ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        {openFaq === i && (
                          <div className="px-6 pb-4">
                            <p className="text-gray-700">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>

                {/* CTA Section */}
                <section className="bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] rounded-2xl p-8 text-white mb-16">
                  <h2 className="text-2xl font-bold mb-4">Bien s'équiper : machines, outils et instrumentation</h2>
                  <p className="mb-6 text-white/90">
                    Le bon matériel de forage, c'est l'ensemble de la chaîne : une machine adaptée, l'outil qui 
                    correspond au terrain, des accessoires compatibles, et une instrumentation fiable et étalonnée 
                    pour tracer l'exécution.
                  </p>
                  <p className="mb-6 text-white/90">
                    GEOTS met en relation les acteurs de la géotechnique et des fondations spéciales : loueurs et 
                    vendeurs de matériel de forage, spécialistes de l'instrumentation, entreprises de travaux et 
                    bureaux d'études.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link to="/shop?listing_type=location" className="bg-white text-[#635BFF] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2">
                      Louer du matériel
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                    <Link to="/shop?listing_type=neuf,occasion" className="bg-white/20 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors">
                      Acheter du matériel
                    </Link>
                  </div>
                </section>

                {/* Related Guides */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#0A2540] mb-6">Pour aller plus loin</h2>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Link 
                      to="/guides/comment-choisir-foreuse-geotechnique"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Comment choisir une foreuse géotechnique ?</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                    <Link 
                      to="/guides/comment-choisir-etude-geotechnique"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Comment choisir son étude géotechnique ?</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                    <Link 
                      to="/guides/etalonnage-capteurs-forage"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Étalonnage des capteurs de forage</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                  </div>
                </section>

                {/* Footer note */}
                <div className="text-center text-gray-500 text-sm border-t pt-8">
                  <p>
                    Ce guide est fourni à titre informatif. Le choix du matériel de forage doit s'appuyer 
                    sur une étude de sol et l'avis de professionnels qualifiés.
                  </p>
                  <p className="mt-2">Dernière mise à jour : 6 juillet 2026</p>
                </div>
              </article>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default DrillingEquipmentGuide;
